/**
 * Makes every key optional, at every object depth.
 *
 * Primitives are leaves and are checked before the object branch, as in
 * `DeepReadonly`: a branded primitive such as `EntityId<T>`
 * (`string & { _brand: T }`) also extends `object`, and mapping over it would
 * yield a type that a plain string satisfies. `Date` and arrays are leaves too;
 * a patch replaces an array as a whole.
 */
export type DeepPartial<T> = T extends
  | Date
  | readonly unknown[]
  | string
  | number
  | boolean
  | bigint
  | symbol
  ? T
  : T extends object
    ? { [K in keyof T]?: DeepPartial<T[K]> }
    : T;

export const ATOM_PATCH_DELETE_SENTINEL_KEY = "__atomPatchDelete";

/**
 * Framework-owned keys are prefixed with `__` and are never business data.
 *
 * Treat this as the single deny-by-default rule: anything matching it is
 * stripped at every boundary unless a boundary explicitly allowlists it.
 */
export const isInternalAtomKey = (key: string): boolean => key.startsWith("__");

/**
 * Internal metadata keys that live ON a versioned atom value but are NOT part
 * of its business data: `__history`/`__latestMeta` are owned by the persistence
 * service, and `__schemaVersion` is stamped onto `latest` by the persist proc
 * (`jsonb_set(latest,'{__schemaVersion}',…)`). They must be stripped from BOTH
 * sides of the old-vs-new diff so a plain business edit never emits a spurious
 * add/remove of a metadata key into the `changes` audit patch.
 */
export const VERSIONED_META_KEYS = [
  "__history",
  "__latestMeta",
  "__schemaVersion",
] as const;

/**
 * The only internal keys allowed to cross the wire outbound. `__history` is
 * additionally gated behind the `includeHistory` read parameter; everything
 * else matching {@link isInternalAtomKey} is dropped before serialization.
 */
export const WIRE_META_ALLOWLIST = ["__latestMeta", "__history"] as const;

export const ATOM_PATCH_DELETE_SENTINEL = {
  [ATOM_PATCH_DELETE_SENTINEL_KEY]: true,
} as const;

export type AtomPatchDeleteSentinel = typeof ATOM_PATCH_DELETE_SENTINEL;

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    !(value instanceof Date)
  );
};

export function isAtomPatchDeleteSentinel(
  value: unknown,
): value is AtomPatchDeleteSentinel {
  return (
    isPlainObject(value) &&
    Object.keys(value).length === 1 &&
    value[ATOM_PATCH_DELETE_SENTINEL_KEY] === true
  );
}

/**
 * Recursively drop every framework-owned key from a value.
 *
 * Returns the SAME reference when nothing was removed, so already-clean values
 * cost no allocation. Callers must therefore treat the result as read-only
 * unless they passed in a copy they own.
 */
export function stripAtomMetadata<T>(value: T): T {
  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((entry) => {
      const stripped = stripAtomMetadata(entry);
      if (stripped !== entry) {
        changed = true;
      }
      return stripped;
    });
    return changed ? (next as T) : value;
  }
  if (!isPlainObject(value)) {
    return value;
  }

  let changed = false;
  const result: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (isInternalAtomKey(key)) {
      changed = true;
      continue;
    }
    const nested = stripAtomMetadata(entry);
    if (nested !== entry) {
      changed = true;
    }
    result[key] = nested;
  }
  return changed ? (result as T) : value;
}

/**
 * The shape `value` takes after a JSON round trip: object keys holding
 * `undefined` are dropped, and `undefined` array slots (holes included) become
 * `null`. Dates and other non-plain objects are left alone.
 *
 * Drafts are compared against the server echo, and that echo has been through
 * JSON: a draft that still holds `undefined` can never equal it. Normalizing
 * every draft write keeps the two comparable.
 *
 * Returns the SAME reference when `value` already has that shape, so clean
 * values cost no allocation and keep their identity for memoization.
 */
export function toJsonShape<T>(value: T): T {
  if (Array.isArray(value)) {
    let changed = false;
    const next: unknown[] = new Array(value.length);
    // Index loop, not `map`: `map` skips holes, which JSON also turns into null.
    for (let index = 0; index < value.length; index++) {
      const entry = value[index];
      const shaped = entry === undefined ? null : toJsonShape(entry);
      if (shaped !== entry || !(index in value)) {
        changed = true;
      }
      next[index] = shaped;
    }
    return changed ? (next as T) : value;
  }
  if (!isPlainObject(value)) {
    return value;
  }

  let changed = false;
  const result: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (entry === undefined) {
      changed = true;
      continue;
    }
    const shaped = toJsonShape(entry);
    if (shaped !== entry) {
      changed = true;
    }
    if (key === "__proto__") {
      // JSON.parse makes `__proto__` an own data key; plain assignment would
      // hit the prototype setter instead and drop the key.
      Object.defineProperty(result, key, {
        value: shaped,
        enumerable: true,
        writable: true,
        configurable: true,
      });
    } else {
      result[key] = shaped;
    }
  }
  return changed ? (result as T) : value;
}

const toComparableTime = (value: unknown): number | null => {
  if (value instanceof Date) {
    const time = value.getTime();
    return Number.isNaN(time) ? null : time;
  }
  if (typeof value === "string") {
    const time = Date.parse(value);
    return Number.isNaN(time) ? null : time;
  }
  return null;
};

/**
 * `record[key]` if it is an own property. Plain indexing would read inherited
 * members, so an entry named `toString` would never count as removed.
 */
const ownValue = (record: Record<string, unknown>, key: string): unknown =>
  Object.hasOwn(record, key) ? record[key] : undefined;

const definedKeys = (value: Record<string, unknown>): string[] =>
  Object.keys(value).filter((key) => value[key] !== undefined);

/**
 * Deep equality of two atom values, as the server stores them.
 *
 * A `Date` equals a string that parses to the same time (antd writes `Date`,
 * shadcn ISO strings, the server rehydrates `Date`), at any depth, arrays
 * included. Two strings compare as strings. An object key holding `undefined`
 * counts as absent, as after a JSON round trip.
 */
export function atomValuesEqual(left: unknown, right: unknown): boolean {
  if (left === right) {
    return true;
  }
  if (left instanceof Date || right instanceof Date) {
    const leftTime = toComparableTime(left);
    return leftTime !== null && leftTime === toComparableTime(right);
  }
  if (Array.isArray(left) || Array.isArray(right)) {
    return (
      Array.isArray(left) &&
      Array.isArray(right) &&
      left.length === right.length &&
      left.every((entry, index) => atomValuesEqual(entry, right[index]))
    );
  }
  if (isPlainObject(left) && isPlainObject(right)) {
    const leftKeys = definedKeys(left);
    if (leftKeys.length !== definedKeys(right).length) {
      return false;
    }
    return leftKeys.every((key) => {
      const rightValue = ownValue(right, key);
      return rightValue !== undefined && atomValuesEqual(left[key], rightValue);
    });
  }
  return Number.isNaN(left) && Number.isNaN(right);
}

/**
 * One unit of change between two atom values: a top-level field, or one
 * direct entry of a top-level plain object (a record such as `activities`).
 */
export interface AtomChange {
  field: string;
  /** Set when the change is one entry of a record-like top-level field. */
  entry?: string;
  /** The new value; `undefined` means the key was removed. */
  value: unknown;
}

/**
 * Mark every key that `baseline` has and `value` lacks with `undefined`,
 * recursing through plain objects present on both sides. Arrays and non-plain
 * values are replaced whole by a patch, so nothing inside them is marked.
 *
 * A patch merges a plain object into what is stored, so a changed entry that
 * is sent whole would keep the keys the user removed inside it.
 */
function markRemovedKeys(value: unknown, baseline: unknown): unknown {
  if (!isPlainObject(value) || !isPlainObject(baseline)) {
    return value;
  }
  const result: Record<string, unknown> = { ...value };
  for (const [key, baselineEntry] of Object.entries(baseline)) {
    if (baselineEntry === undefined) {
      continue;
    }
    const valueEntry = ownValue(value, key);
    result[key] =
      valueEntry === undefined
        ? undefined
        : markRemovedKeys(valueEntry, baselineEntry);
  }
  return result;
}

const toBusinessRecord = (value: unknown): Record<string, unknown> => {
  const stripped = stripAtomMetadata(value);
  return isPlainObject(stripped) ? stripped : {};
};

const unionKeys = (
  left: Record<string, unknown>,
  right: Record<string, unknown>,
): string[] => [...new Set([...Object.keys(left), ...Object.keys(right)])];

function collectEntryChanges(
  field: string,
  baseline: Record<string, unknown>,
  value: Record<string, unknown>,
): AtomChange[] {
  const changes: AtomChange[] = [];
  for (const entry of unionKeys(baseline, value)) {
    const baselineEntry = ownValue(baseline, entry);
    const valueEntry = ownValue(value, entry);
    if (valueEntry === undefined) {
      if (baselineEntry !== undefined) {
        changes.push({ field, entry, value: undefined });
      }
    } else if (!atomValuesEqual(baselineEntry, valueEntry)) {
      changes.push({
        field,
        entry,
        value: markRemovedKeys(valueEntry, baselineEntry),
      });
    }
  }
  return changes;
}

/**
 * What changed from `baseline` to `value`, at the granularity a form save
 * writes: every top-level field, and for a field that is a plain object on
 * both sides each of its direct entries. A changed entry is reported whole
 * (with the keys removed inside it marked `undefined`), so a patch re-creates
 * a complete entry rather than a fragment. A removed key, on either level,
 * is reported with `value: undefined`.
 *
 * Framework-owned (`__`) keys are stripped from both sides first. A field
 * changed and changed back is not a change.
 */
export function collectAtomChanges(
  baseline: unknown,
  value: unknown,
): AtomChange[] {
  const baselineRecord = toBusinessRecord(baseline);
  const valueRecord = toBusinessRecord(value);
  const changes: AtomChange[] = [];
  for (const field of unionKeys(baselineRecord, valueRecord)) {
    const baselineField = ownValue(baselineRecord, field);
    const valueField = ownValue(valueRecord, field);
    if (valueField === undefined) {
      if (baselineField !== undefined) {
        changes.push({ field, value: undefined });
      }
    } else if (isPlainObject(baselineField) && isPlainObject(valueField)) {
      changes.push(...collectEntryChanges(field, baselineField, valueField));
    } else if (!atomValuesEqual(baselineField, valueField)) {
      changes.push({ field, value: valueField });
    }
  }
  return changes;
}

/** Build the patch that writes `changes` (removals as `undefined`). */
export function changesToPatch<T>(
  changes: readonly AtomChange[],
): DeepPartial<T> {
  const patch: Record<string, unknown> = {};
  for (const { field, entry, value } of changes) {
    if (entry === undefined) {
      patch[field] = value;
      continue;
    }
    const current = ownValue(patch, field);
    const entries = isPlainObject(current) ? current : {};
    entries[entry] = value;
    patch[field] = entries;
  }
  return patch as DeepPartial<T>;
}

/**
 * The patch that turns `baseline` into `value`, see
 * {@link collectAtomChanges}. Removed keys are `undefined`, which
 * {@link serializePatchForTransport} sends as a delete, so do not JSON-clone
 * the result.
 */
export function diffForPatch<T>(
  baseline: T | null | undefined,
  value: T,
): DeepPartial<T> {
  return changesToPatch<T>(collectAtomChanges(baseline, value));
}

/** The live value at the place a change writes: its field or its entry. */
const liveValueAt = (live: unknown, change: AtomChange): unknown => {
  const liveField = isPlainObject(live)
    ? ownValue(live, change.field)
    : undefined;
  if (change.entry === undefined) {
    return liveField;
  }
  return isPlainObject(liveField)
    ? ownValue(liveField, change.entry)
    : undefined;
};

/**
 * The patch that overwrites a conflict with the user's version: like
 * {@link diffForPatch}, but a change that overlaps one in `live` also marks
 * the keys `live` has there and the user's value lacks, so the merge stores
 * exactly the user's field or entry. Other changes stay as they are, and
 * what `live` changed elsewhere is not touched.
 */
export function diffForOverwrite<T>(
  baseline: T | null | undefined,
  value: T,
  live: T | null | undefined,
): DeepPartial<T> {
  const valueRecord = toBusinessRecord(value);
  const incoming = collectAtomChanges(baseline, live);
  // A field the other user replaced or removed as a whole is written whole,
  // even where the user changed only some of its entries.
  const wholeFields = new Set(
    incoming.filter((change) => change.entry === undefined).map((c) => c.field),
  );
  const own: AtomChange[] = [];
  for (const change of collectAtomChanges(baseline, value)) {
    if (change.entry === undefined || !wholeFields.has(change.field)) {
      own.push(change);
    } else if (!own.some((other) => other.field === change.field)) {
      own.push({
        field: change.field,
        value: ownValue(valueRecord, change.field),
      });
    }
  }
  return changesToPatch<T>(
    own.map((change) =>
      incoming.some((other) => changesOverlap(change, other))
        ? {
            ...change,
            value: markRemovedKeys(change.value, liveValueAt(live, change)),
          }
        : change,
    ),
  );
}

function changesOverlap(left: AtomChange, right: AtomChange): boolean {
  return (
    left.field === right.field &&
    (left.entry === undefined ||
      right.entry === undefined ||
      left.entry === right.entry)
  );
}

const isSameChange = (left: AtomChange, right: AtomChange): boolean =>
  left.field === right.field &&
  left.entry === right.entry &&
  atomValuesEqual(left.value, right.value);

/**
 * Top-level fields that both change sets touch: the same field changed whole
 * on either side, or the same entry of a record. A change both sides made
 * identically is not an overlap.
 */
export function overlappingChangeFields(
  left: readonly AtomChange[],
  right: readonly AtomChange[],
): string[] {
  const fields = new Set<string>();
  for (const leftChange of left) {
    const overlaps = right.some(
      (rightChange) =>
        changesOverlap(leftChange, rightChange) &&
        !isSameChange(leftChange, rightChange),
    );
    if (overlaps) {
      fields.add(leftChange.field);
    }
  }
  return [...fields];
}

/**
 * Apply a patch to a copy of `base`, with the semantics of the server's
 * `patchAtom`: `undefined` and the delete sentinel remove a key, a plain
 * object merges into the stored plain object, anything else replaces.
 * Framework-owned keys of the patch are ignored; those of `base` are kept.
 */
export function applyAtomPatch<T>(base: T, patch: DeepPartial<T>): T {
  if (!isPlainObject(patch) || isAtomPatchDeleteSentinel(patch)) {
    return patch as T;
  }
  const result: Record<string, unknown> = isPlainObject(base)
    ? { ...base }
    : {};
  for (const [key, entry] of Object.entries(patch)) {
    if (isInternalAtomKey(key)) {
      continue;
    }
    if (entry === undefined || isAtomPatchDeleteSentinel(entry)) {
      delete result[key];
    } else if (isPlainObject(entry)) {
      result[key] = applyAtomPatch(ownValue(result, key), entry);
    } else {
      result[key] = entry;
    }
  }
  return result as T;
}

/**
 * The value one unit of a patch had where the change was made: a top-level
 * field, or one direct entry of it (see {@link collectAtomChanges}). Sent
 * with a patch as `expected`, so the server can reject a save built on a
 * value that changed since.
 */
export interface AtomPatchExpectation {
  /** Top-level field. */
  field: string;
  /** Direct entry of `field`, when the unit is a record entry. */
  entry?: string;
  /**
   * The value the unit had. `null`, `undefined` and a missing key all mean
   * "absent or null": a unit compares null and absent as equal.
   */
  value?: unknown;
}

export type AtomPatchExpectations = readonly AtomPatchExpectation[];

/** One unit a patch writes, with its value before and after the patch. */
export interface AtomPatchUnit {
  field: string;
  entry?: string;
  before: unknown;
  after: unknown;
}

/** Longest field or entry name {@link readPatchExpectations} accepts. */
export const ATOM_PATCH_EXPECTATION_KEY_MAX_LENGTH = 256;

/** Most units {@link readPatchExpectations} accepts in one `expected`. */
export const ATOM_PATCH_EXPECTATIONS_MAX_COUNT = 10_000;

const UNSAFE_UNIT_KEYS: ReadonlySet<string> = new Set([
  "__proto__",
  "constructor",
  "prototype",
]);

/** A field or entry name a unit may have: no `__` key, no prototype key. */
export const isSafePatchUnitKey = (key: string): boolean =>
  !isInternalAtomKey(key) && !UNSAFE_UNIT_KEYS.has(key);

/** A patch value that writes a record entry by entry, not the whole field. */
const isMergingPatchValue = (
  value: unknown,
): value is Record<string, unknown> =>
  isPlainObject(value) && !isAtomPatchDeleteSentinel(value);

const businessKeysOf = (record: Record<string, unknown>): string[] =>
  Object.keys(record).filter((key) => !isInternalAtomKey(key));

const entryOf = (fieldValue: unknown, entry: string): unknown =>
  isPlainObject(fieldValue) ? ownValue(fieldValue, entry) : undefined;

/**
 * The units `patch` writes over `base`, at the granularity of
 * {@link collectAtomChanges}: a key whose patch value is a plain object (not
 * the delete sentinel) and whose base value is a plain object is written
 * entry by entry; any other key is one unit. `before` is the unit in `base`,
 * `after` the unit in {@link applyAtomPatch}`(base, patch)`. Framework-owned
 * (`__`) keys are skipped, on both levels. `undefined` and the delete
 * sentinel both remove.
 */
export function patchUnitsOf(base: unknown, patch: unknown): AtomPatchUnit[] {
  if (!isMergingPatchValue(patch)) {
    return [];
  }
  const baseRecord = toBusinessRecord(base);
  const applied = applyAtomPatch<Record<string, unknown>>(baseRecord, patch);
  const units: AtomPatchUnit[] = [];
  for (const field of businessKeysOf(patch)) {
    const patchField = patch[field];
    const baseField = ownValue(baseRecord, field);
    const appliedField = ownValue(applied, field);
    if (isMergingPatchValue(patchField) && isPlainObject(baseField)) {
      for (const entry of businessKeysOf(patchField)) {
        units.push({
          field,
          entry,
          before: ownValue(baseField, entry),
          after: entryOf(appliedField, entry),
        });
      }
    } else {
      units.push({ field, before: baseField, after: appliedField });
    }
  }
  return units;
}

/**
 * `expected` for `patch` over `base`: every unit the patch writes, with the
 * value it has in `base`.
 */
export function patchExpectationsFor(
  base: unknown,
  patch: unknown,
): AtomPatchExpectation[] {
  return patchUnitsOf(base, patch).map(({ field, entry, before }) => ({
    field,
    ...(entry === undefined ? {} : { entry }),
    ...(before === undefined ? {} : { value: before }),
  }));
}

const isAbsentUnit = (value: unknown): boolean =>
  value === undefined || value === null;

/**
 * Equality of two values of one unit: {@link atomValuesEqual}, except that
 * `null` and absent are equal at the unit itself (not deeper).
 */
export function patchUnitValuesEqual(left: unknown, right: unknown): boolean {
  if (isAbsentUnit(left) || isAbsentUnit(right)) {
    return isAbsentUnit(left) && isAbsentUnit(right);
  }
  return atomValuesEqual(left, right);
}

/**
 * What a patch writes, by field: `true` for the whole field, else the
 * entries it writes.
 */
function writtenUnitsByField(
  units: readonly AtomPatchUnit[],
): Map<string, true | Set<string>> {
  const byField = new Map<string, true | Set<string>>();
  for (const { field, entry } of units) {
    const known = byField.get(field);
    if (entry === undefined) {
      byField.set(field, true);
    } else if (known === undefined) {
      byField.set(field, new Set([entry]));
    } else if (known !== true) {
      known.add(entry);
    }
  }
  return byField;
}

/** Whether a patch that writes `written` writes the unit an expectation names. */
const writesExpectedUnit = (
  written: Map<string, true | Set<string>>,
  { field, entry }: AtomPatchExpectation,
): boolean => {
  const entries = written.get(field);
  return (
    entries !== undefined &&
    (entry === undefined || entries === true || entries.has(entry))
  );
};

/**
 * Whether the stored unit is neither the expected value nor the value the
 * patch writes. An entry expectation also fails when its field is no longer
 * a record and the patch would replace it: the entry was built on a record.
 */
function isExpectationStale(
  currentRecord: Record<string, unknown>,
  applied: Record<string, unknown>,
  { field, entry, value }: AtomPatchExpectation,
): boolean {
  const currentField = ownValue(currentRecord, field);
  const appliedField = ownValue(applied, field);
  if (entry === undefined) {
    return (
      !patchUnitValuesEqual(currentField, value) &&
      !patchUnitValuesEqual(currentField, appliedField)
    );
  }
  if (
    !isPlainObject(currentField) &&
    !patchUnitValuesEqual(currentField, appliedField)
  ) {
    return true;
  }
  const stored = entryOf(currentField, entry);
  return (
    !patchUnitValuesEqual(stored, value) &&
    !patchUnitValuesEqual(stored, entryOf(appliedField, entry))
  );
}

/**
 * The units of `patch` that are stale against `current`: those `expected`
 * names and the patch writes, whose current value is neither the expected
 * value nor the value the patch writes there. An expectation of a whole field
 * covers every entry the patch writes in it; an expectation of an entry also
 * covers a patch that writes or deletes its field whole. Units without an
 * expectation are not checked. Pure: never mutates its inputs.
 */
export function findStalePatchUnits(
  current: unknown,
  patch: unknown,
  expected: AtomPatchExpectations,
): Array<{ field: string; entry?: string }> {
  const units = patchUnitsOf(current, patch);
  if (units.length === 0) {
    return [];
  }
  const currentRecord = toBusinessRecord(current);
  const applied = applyAtomPatch<Record<string, unknown>>(
    currentRecord,
    patch as DeepPartial<Record<string, unknown>>,
  );
  const written = writtenUnitsByField(units);
  const stale: Array<{ field: string; entry?: string }> = [];
  for (const expectation of expected) {
    if (
      writesExpectedUnit(written, expectation) &&
      isExpectationStale(currentRecord, applied, expectation)
    ) {
      const { field, entry } = expectation;
      stale.push(entry === undefined ? { field } : { field, entry });
    }
  }
  return stale;
}

const EXPECTATION_KEYS: ReadonlySet<string> = new Set([
  "field",
  "entry",
  "value",
]);

const isUnitName = (value: unknown, minLength: number): value is string =>
  typeof value === "string" &&
  value.length >= minLength &&
  value.length <= ATOM_PATCH_EXPECTATION_KEY_MAX_LENGTH &&
  isSafePatchUnitKey(value);

function readPatchExpectation(raw: unknown): AtomPatchExpectation | null {
  if (!isPlainObject(raw)) {
    return null;
  }
  if (Object.keys(raw).some((key) => !EXPECTATION_KEYS.has(key))) {
    return null;
  }
  const { field, entry, value } = raw;
  if (!isUnitName(field, 1)) {
    return null;
  }
  if (entry === undefined) {
    return {
      field,
      // Compared with the stored business data, which has no `__` keys.
      ...(value === undefined ? {} : { value: stripAtomMetadata(value) }),
    };
  }
  if (!isUnitName(entry, 0)) {
    return null;
  }
  return {
    field,
    entry,
    // Compared with the stored business data, which has no `__` keys.
    ...(value === undefined ? {} : { value: stripAtomMetadata(value) }),
  };
}

/**
 * Reads untrusted `expected` input: a list of {@link AtomPatchExpectation}
 * with safe field and entry names. Returns a new list (values without `__`
 * keys), or `null` when the input does not have that shape. Never throws.
 */
export function readPatchExpectations(
  raw: unknown,
): AtomPatchExpectations | null {
  if (!Array.isArray(raw) || raw.length > ATOM_PATCH_EXPECTATIONS_MAX_COUNT) {
    return null;
  }
  const expectations: AtomPatchExpectation[] = [];
  for (const item of raw) {
    const expectation = readPatchExpectation(item);
    if (expectation === null) {
      return null;
    }
    expectations.push(expectation);
  }
  return expectations;
}

/**
 * Resolve a conflicting field by taking the other user's version of what both
 * changed, while keeping the user's own changes to other entries of it.
 */
export function resolveFieldWithTheirs(
  field: string,
  baseline: unknown,
  mine: unknown,
  theirs: unknown,
): unknown {
  const pickField = (value: unknown) => ({
    [field]: isPlainObject(value) ? ownValue(value, field) : undefined,
  });
  const own = collectAtomChanges(pickField(baseline), pickField(mine));
  const incoming = collectAtomChanges(pickField(baseline), {
    [field]: theirs,
  });
  const kept = own.filter(
    (change) => !incoming.some((other) => changesOverlap(change, other)),
  );
  const resolved = applyAtomPatch<Record<string, unknown>>(
    { [field]: theirs },
    changesToPatch(kept),
  );
  return resolved[field];
}

/**
 * Options for every outbound (wire-facing) filter.
 *
 * `includeHistory` is deliberately REQUIRED. Each outbound path must state what
 * the caller asked for; four separate review rounds on this branch were caused
 * by a call site silently defaulting it, so the compiler now forces the choice.
 */
export interface WireFilterOptions {
  includeHistory: boolean;
}

/** Keys the outbound policy permits at the TOP LEVEL of an atom value. */
const isAllowedAtTopLevel =
  ({ includeHistory }: WireFilterOptions) =>
  (key: string): boolean =>
    key === "__latestMeta" || (key === "__history" && includeHistory);

/**
 * Recursively drop framework-owned keys from a value on its way OUT to a client.
 *
 * Differs from {@link stripAtomMetadata} only at the top level, where the atom's
 * own metadata is legitimate: `__latestMeta` always survives and `__history`
 * survives when explicitly requested. Every nested level is stripped outright —
 * nested internals are never business data, and because inbound writes go
 * through the fully-recursive {@link stripAtomMetadata}, anything left nested on
 * the way out could never round-trip. That asymmetry is what wedges MagicForm
 * drafts into a permanent phantom diff, so keep the two directions symmetric.
 *
 * Returns the SAME reference when nothing was removed, so the common (clean)
 * case allocates nothing on hot subscription paths and callers can rely on
 * identity to detect a no-op.
 */
export function stripInternalAtomFieldsForWire<T>(
  value: T,
  options: WireFilterOptions,
): T {
  // Arrays are a legitimate payload shape (a bare list of atoms). Recurse rather
  // than passing them through — treating "not a plain object" as "nothing to do"
  // silently exempted every array-shaped payload from the policy.
  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((entry) => {
      const stripped = stripInternalAtomFieldsForWire(entry, options);
      if (stripped !== entry) {
        changed = true;
      }
      return stripped;
    });
    return changed ? (next as T) : value;
  }
  if (!isPlainObject(value)) {
    return value;
  }

  const isAllowed = isAllowedAtTopLevel(options);

  let changed = false;
  const result: Record<string, unknown> = {};

  for (const [key, entry] of Object.entries(value)) {
    if (isInternalAtomKey(key) && !isAllowed(key)) {
      changed = true;
      continue;
    }
    // Allowlisted metadata is framework-owned and passed through untouched;
    // everything else is business data whose interior must be stripped.
    const nested = isAllowed(key) ? entry : stripAtomMetadata(entry);
    if (nested !== entry) {
      changed = true;
    }
    result[key] = nested;
  }

  return changed ? (result as T) : value;
}

/**
 * Apply the outbound internal-field policy to raw JSON-Patch ops.
 *
 * `__partialUpdate` atoms ship their patches to the client without going
 * through the transform pipeline, so the policy has to be enforced here.
 *
 * Every path segment is checked, not just the first: an op like
 * `add /entries/0/__schemaVersion` targets a perfectly ordinary `entries` array
 * yet still carries an internal key, and a primitive value cannot be scrubbed
 * by stripping. Such ops are dropped outright — the client never received those
 * keys, so the op would either leak an internal value or fail to apply against
 * state where the field was already stripped.
 *
 * Allowlisting matches {@link stripInternalAtomFieldsForWire}: `__latestMeta`
 * always survives and `__history` survives when the subscriber asked for it —
 * and, as there, an allowlisted subtree is passed through UNTOUCHED. Stripping
 * inside it would make an `/__history/-` op deliver different data than the same
 * changelog delivered as a full value.
 */
export function filterInternalFieldPatches<
  PatchType extends { path: string; from?: string; value?: unknown },
>(patches: PatchType[], options: WireFilterOptions): PatchType[] {
  const isAllowed = isAllowedAtTopLevel(options);

  /** True when a JSON Pointer targets, or reaches through, a denied key. */
  const pointerTouchesDeniedKey = (pointer: string): boolean => {
    const segments = pointer.split("/").slice(1);
    const [first] = segments;
    if (first !== undefined && isAllowed(first)) {
      // Allowlisted subtree — everything below it travels with it.
      return false;
    }
    return segments.some((segment) => isInternalAtomKey(segment));
  };

  /** Which strip applies to the value an op carries, given where it lands. */
  const scrubOpValue = (path: string, value: unknown): unknown => {
    // Only the empty pointer addresses the whole document; "/" addresses the
    // key "". A root op carries the whole atom, so the top-level allowlist
    // applies.
    if (path === "") {
      return stripInternalAtomFieldsForWire(value, options);
    }
    // An allowlisted subtree travels untouched, matching the value filter.
    const [firstSegment] = path.split("/").slice(1);
    if (firstSegment !== undefined && isAllowed(firstSegment)) {
      return value;
    }
    // Anything else is business data, stripped outright.
    return stripAtomMetadata(value);
  };

  return patches
    .filter((patch) => {
      // `move`/`copy` carry a second pointer in `from`. Checking only `path`
      // let `{op:"move", from:"/__schemaVersion", path:"/leaked"}` relocate an
      // internal value into business data, and `{op:"copy", from:"/__history/0"}`
      // additionally throws OPERATION_FROM_UNRESOLVABLE on a client whose copy
      // of that key was stripped — killing the subscription.
      if (patch.from !== undefined && pointerTouchesDeniedKey(patch.from)) {
        return false;
      }
      return !pointerTouchesDeniedKey(patch.path);
    })
    .map((patch) => {
      if (!("value" in patch)) {
        return patch;
      }
      const value = scrubOpValue(patch.path, patch.value);
      return value === patch.value ? patch : { ...patch, value };
    });
}

export function serializePatchForTransport<T>(value: T): T {
  if (value === undefined) {
    return ATOM_PATCH_DELETE_SENTINEL as T;
  }
  if (value === null || value instanceof Date) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((entry) => serializePatchForTransport(entry)) as T;
  }
  if (isAtomPatchDeleteSentinel(value)) {
    return value;
  }
  if (typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([key]) => !isInternalAtomKey(key))
        .map(([key, entry]) => [key, serializePatchForTransport(entry)]),
    ) as T;
  }
  return value;
}
